<template>
  <section class="coupon-editor">
    <div class="input-container">
      <label class="label">รหัสส่วนลด</label>
      <el-input
        :value="value.code"
        :disabled="value.isActionEdit"
        class="input"
        @input="$emit('input', { ...value, code: $event })"
      ></el-input>
    </div>
    <div class="input-container">
      <label class="label">ประเภทส่วนลด</label>
      <el-radio
        v-for="type in couponType"
        :key="type.value"
        v-model="selectedCouponType"
        :label="type.value"
        :disabled="value.isActionEdit"
      >{{ type.label }}</el-radio>
    </div>
    <div class="input-container">
      <label class="label">ยอดลด</label>
      <el-input
        v-model="discount"
        :disabled="value.isActionEdit"
        class="input"
        type="number"
        min="0"
      ></el-input>
      {{ discountUnit }}
    </div>
    <div class="input-container">
      <label class="label">จำนวนสิทธิ์</label>
      <el-checkbox v-model="quotaNoLimit">ไม่จำกัด</el-checkbox>
      <el-input
        v-if="!quotaNoLimit"
        v-model="usageQuota"
        :disabled="value.isActionEdit"
        class="input"
        type="number"
        min="0"
      ></el-input>
    </div>
    <div class="input-container">
      <label class="label">จำนวนสิทธิ์ต่อคน</label>
      <el-checkbox v-model="quotaPerPersonDefault">ค่าเริ่มต้น 1 สิทธิ</el-checkbox>
      <el-input
        v-if="!quotaPerPersonDefault"
        v-model="usageQuotaPerUser"
        :disabled="value.isActionEdit"
        class="input"
        type="number"
        min="1"
      ></el-input>
    </div>
    <div class="input-container">
      <label class="label">&nbsp;</label>
      <el-checkbox
        v-model="isFirstTimeOnly"
        :disabled="value.isActionEdit"
      >สำหรับลูกค้าซื้อครั้งแรกเท่านั้น
      </el-checkbox>
    </div>
    <div class="input-container">
      <div>
        <label class="label">ระยะเวลาใช้งาน</label>
        <label class="usage-date-range-label">วันที่เริ่ม</label>
        <date-picker
          v-model="effectiveDate"
          :disabled="value.isActionEdit"
        ></date-picker>
      </div>
      <div>
        <label class="label">&nbsp;</label>
        <el-radio v-model="periodType" label="expiryDate" class="usage-date-range-label">วันที่สิ้นสุด</el-radio>
        <date-picker
          v-model="expiryDate"
          :disabled="periodType !== 'expiryDate' || isExpired"
          class="top-margin"
        ></date-picker>
      </div>
      <div>
        <label class="label">&nbsp;</label>
        <el-radio
          v-model="periodType"
          :disabled="value.isActionEdit"
          label="transferDate"
          class="usage-date-range-label"
        >นับจากวันที่โอน</el-radio>
        <el-input
          v-model="expiredDaysAfterTransfered"
          :disabled="periodType !== 'transferDate'"
          class="input top-margin"
          type="number"
        ></el-input> วัน
      </div>
    </div>
    <div class="input-container">
      <label class="label">ยอดซื้อขั้นต่ำ</label>
      <el-input
        v-model="minimumPurchasedAmount"
        :disabled="value.isActionEdit"
        class="input"
        type="number"
        min="0"
      ></el-input>
      {{ "บาท" }}
    </div>
    <div v-if="showMaximumDiscountAmount" class="input-container">
      <label class="label">ยอดลดสูงสุด</label>
      <el-input
        v-model="maximumDiscountAmount"
        :disabled="value.isActionEdit"
        class="input"
        type="number"
        min="0"
      ></el-input>
      {{ "บาท" }}
    </div>
    <div class="input-container row">
      <label class="label col-12">แพ็กเกจที่ร่วมรายการ</label>
      <package-selector
        v-model="selectedPackages"
        :is-action-edit="value.isActionEdit"
        :excluded-packages="excludedPackages"
        :is-coupon-page="true"
      >
      </package-selector>
    </div>
    <div class="input-container">
      <label class="label">โครงการที่รองรับ</label>
      <br />
      <el-checkbox v-model="isSupportAllProjects">รองรับทุกโครงการ</el-checkbox>
      <project-selector
        v-show="!isSupportAllProjects"
        :value="supportedProjects"
        :disable-delete-all="disableProjectDeleteAll"
        @input="supportedProjectsChange"
      ></project-selector>
    </div>
  </section>
</template>

<script>
import {
  Input as ElInput,
  Radio as ElRadio,
  Checkbox as ElCheckbox
} from "element-ui";
import DatePicker from "@/lib/components/DatePicker";
import ProjectSelector from "@/lib/components/ProjectSelector";
import PackageSelector from "../../collections/components/PackageSelector";

export default {
  components: {
    ElInput,
    ElRadio,
    ElCheckbox,
    DatePicker,
    PackageSelector,
    ProjectSelector
  },
  props: {
    value: {
      type: Object,
      default: () => ({})
    },
    disableProjectDeleteAll: {
      type: Boolean,
      default: false
    }
  },
  data() {
    const couponType = [
      { label: "ส่วนลดเป็นจำนวนเงิน", value: "flat" },
      { label: "ส่วนลดเป็น %", value: "percentage" }
    ];

    const {
      discountAmount,
      discountPercentAmount,
      usageQuota,
      usageQuotaPerUser,
      selectedPackages,
      supportedProjectIds,
      effectiveDate,
      expiryDate,
      expiredDaysAfterTransfered,
      maximumDiscountAmount,
      minimumPurchasedAmount,
      isFirstTimeOnly,
      isExpired,
      excludedPackages
    } = this.value;

    const selectedCouponType = discountPercentAmount ? "percentage" : "flat";
    const discount =
      selectedCouponType === "flat"
        ? discountAmount / 100
        : discountPercentAmount;
    const quotaNoLimit = !usageQuota;
    const quotaPerPersonDefault = !usageQuotaPerUser;
    const isSupportAllProjects = !supportedProjectIds;
    let periodType;
    if (expiredDaysAfterTransfered !== null) {
      periodType = 'transferDate';
    } else {
      periodType = 'expiryDate';
    }

    return {
      couponType,
      selectedCouponType,
      discount,
      quotaNoLimit,
      quotaPerPersonDefault,
      selectedPackages,
      effectiveDate,
      expiryDate,
      expiredDaysAfterTransfered,
      maximumDiscountAmount:
        maximumDiscountAmount && maximumDiscountAmount !== 0
          ? maximumDiscountAmount / 100
          : maximumDiscountAmount,
      minimumPurchasedAmount:
        minimumPurchasedAmount && minimumPurchasedAmount !== 0
          ? minimumPurchasedAmount / 100
          : minimumPurchasedAmount,
      usageQuota,
      usageQuotaPerUser,
      isSupportAllProjects,
      periodType,
      isFirstTimeOnly,
      isExpired,
      excludedPackages
    };
  },
  computed: {
    discountUnit() {
      const unit = {
        flat: "บาท",
        percentage: "%"
      };

      return unit[this.selectedCouponType];
    },
    showMaximumDiscountAmount() {
      return !this.isFlatRate;
    },
    isFlatRate() {
      return this.selectedCouponType === this.couponType[0].value;
    },
    supportedProjects() {
      return (this.value.supportedProjectIds || []).map(projectId => ({
        projectId
      }));
    }
  },
  watch: {
    selectedCouponType() {
      this.selectedCouponTypeChange();
    },
    discount(val) {
      this.discountChange(val);
    },
    quotaNoLimit(val) {
      this.quotaNoLimitChange(val);
    },
    quotaPerPersonDefault(val) {
      this.quotaPerPersonDefaultChange(val);
    },
    selectedPackages(val) {
      this.selectedPackagesChange(val);
    },
    minimumPurchasedAmount(val) {
      this.numberValueChange("minimumPurchasedAmount", val * 100);
    },
    maximumDiscountAmount(val) {
      this.numberValueChange("maximumDiscountAmount", val * 100);
    },
    usageQuota(val) {
      this.numberValueChange("usageQuota", val);
    },
    usageQuotaPerUser(val) {
      this.numberValueChange("usageQuotaPerUser", val);
    },
    isSupportAllProjects(val) {
      this.isSupportAllProjectsChange(val);
    },
    effectiveDate(val) {
      this.effectiveDateChange(val);
    },
    expiryDate(val) {
      this.expiryDateChange(val);
    },
    expiredDaysAfterTransfered(val) {
      this.numberValueChange("expiredDaysAfterTransfered", val);
    },
    periodType() {
      this.expiredDaysAfterTransfered = null;
      this.expiryDate = null;
    },
    isFirstTimeOnly(val) {
      this.$emit("input", {
        ...this.value,
        isFirstTimeOnly: val
      });
    }
  },
  methods: {
    selectedCouponTypeChange() {
      if (this.discount === 0) return;

      this.discount = 0;
    },
    discountChange(discount) {
      const emitValue = this.isFlatRate
        ? {
            ...this.value,
            discountAmount: +discount * 100,
            discountPercentAmount: undefined
          }
        : {
            ...this.value,
            discountAmount: undefined,
            discountPercentAmount: +discount
          };

      this.$emit("input", emitValue);
    },
    quotaNoLimitChange(isNoLimit) {
      if (isNoLimit) {
        this.$emit("input", { ...this.value, usageQuota: undefined });
      }
    },
    quotaPerPersonDefaultChange(flag) {
      if (flag) {
        this.$emit("input", { ...this.value, usageQuotaPerUser: undefined });
      }
    },
    selectedPackagesChange(packages) {
      this.$emit("input", {
        ...this.value,
        selectedPackages: packages
      });
    },
    supportedProjectsChange(projects) {
      if (projects) {
        this.$emit("input", {
          ...this.value,
          supportedProjectIds: projects.map(({ projectId }) => projectId)
        });
        return;
      }

      this.$emit("input", { ...this.value, supportedProjectIds: null });
    },
    effectiveDateChange(effectiveDate) {
      this.$emit("input", {
        ...this.value,
        effectiveDate
      });
    },
    expiryDateChange(date) {
      if (!date) {
        this.$emit("input", {
          ...this.value,
          expiryDate: null
        });
        return;
      }

      const adjusted = date.setZone("utc+7").endOf("day").toUTC();
      this.$emit("input", {
        ...this.value,
        expiryDate: adjusted
      });
    },
    numberValueChange(key, val) {
      this.$emit("input", { ...this.value, [key]: val ? +val : val });
    },
    isSupportAllProjectsChange(isSupportAllProjects) {
      if (isSupportAllProjects) {
        this.supportedProjectsChange(null);
        return;
      }

      this.supportedProjectsChange([]);
    }
  }
};
</script>

<style lang="scss" scoped>
.label {
  margin-bottom: 5px;
  font-weight: bold;
  font-size: 14px;

  width: 150px;

  &.sub {
    font-weight: lighter;
  }
}

.top-margin {
  margin-top: 5px;
}


.input {
  width: 100px;
}

.input-container {
  margin-bottom: 15px;
}

.usage-date-range-label {
  width: 130px;
  margin-right: 0px;
}
</style>
